var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import * as Long from "long";
import { selectPackagesByRequiredOfferId } from "../balancingSelectors/selectBalancingPackages";
import { IdPersistence } from "../constants/IdPersistence";
import { promoteMobileLevels } from "../constants/PromoteMobileLevels";
import { webExclusiveProfilePicturePackageId } from "../constants/webExclusives";
import { EventType } from "../features/balancing/types/BalancingCore";
import { IdIcon } from "../features/generic/icon/IdIcon";
import { IdActionButtonEntries } from "../features/hud-action-button/IdActionButtonEntries";
import { mapActionButton } from "../features/hud-action-button/mapActionButton";
import { MapGrowthFundActionButtonIcon } from "../features/hud-action-button/mapGrowthFundActionButtonIcon";
import { mapPendingInventoryTypeToAction } from "../features/pending-inventory/mapPendingInventoryTypeToAction";
import { actionPersistenceWriteBeforeAction } from "../features/persistence/actionPersistence";
import { actionShopOpenOffer } from "../features/shop/actionShop";
import { IdTournamentProgression } from "../features/tournament-building/IdTournamentProgression";
import { LimitedActionType } from "../utils/network/generated/protoTypes";
import { PersistenceMode } from "../utils/PersistenceMode";
import { utilBlog } from "../utils/utilBlog";
import { utilLimitedActionTime } from "../utils/utilLimitedActionTime";
import { utilPersistence } from "../utils/utilPersistence";
import { utilTime } from "../utils/utilTime";
import { selectCoop, selectCoopCompletedProject, selectCurrentCoopLevel, selectHasActiveInvite, } from "./selectCoopInfo";
import { selectHideDiscountEventActionButton } from "./selectDiscountEvent";
import { selectIsQualifiedForDoghouse } from "./selectDoghouseInfo";
import { selectActiveDiscountEventInfoByEventType, selectActiveThemedEventInfo, selectIsEventRunning, selectIsEventRunningByEventType, selectPrimeTimeEventInfo, selectThemedEventCanPlayerJoin, selectThemedEventIsQuestsCompleted, selectThemedEventIsRewardCollected, } from "./selectEventInfo";
import { selectLimitedActionInfo } from "./selectLimitedActionInfo";
import { selectIsOfferPurchased } from "./selectPaymentEntryInfo";
import { selectPendingInventoryCount, selectPendingInventoryInfo, } from "./selectPendingInventoryInfo";
import { selectPlayerLevel } from "./selectPlayerLevelInfo";
import { selectGrowthFundCharacteristicIds, selectGrowthFundRewardInfo } from "./selectShopInfo";
import { selectIsPurchasePackageDataAvailableForPackage, selectTimesPurchasePackageAmount, } from "./selectTimesPurchasePackageAmount";
import { selectTournamentRankProgression } from "./selectTournamentInfo";
import { selectWaterTowerCanCollectWater } from "./selectWatertowerInfo";
export var selectIterations = function (state, id) {
    return state.actionButtonInfos.actionButtonIterations.get(id) || 0;
};
export var selectRotatableActionButtonInfoById = function (state, id) {
    var buttonInfos = mapActionButton.get(id);
    if (buttonInfos === undefined) {
        return;
    }
    var iterations = selectIterations(state, id);
    return __assign({}, buttonInfos, { iterations: iterations });
};
export var selectCurrentActionButton = function (state) {
    return state.actionButtonInfos.currentActionButton;
};
var sortByPriority = function (currentVal, nextVal) {
    if (currentVal.priority < nextVal.priority) {
        return -1;
    }
    if (currentVal.priority > nextVal.priority) {
        return 1;
    }
    return 0;
};
var sortByIterationsAscending = function (currentVal, nextVal) {
    if (currentVal.iterations < nextVal.iterations) {
        return -1;
    }
    if (currentVal.iterations > nextVal.iterations) {
        return 1;
    }
    return 0;
};
export var selectTopActionButton = function (state) {
    var actionButtons = selectAllActionButtons(state).sort(sortByIterationsAscending);
    if (actionButtons.length === 0) {
        return;
    }
    var maxIterations = actionButtons[0].iterations;
    actionButtons = actionButtons.filter(function (element) { return element && element.iterations <= maxIterations; });
    if (actionButtons.length === 0) {
        return;
    }
    return actionButtons.sort(sortByPriority)[0];
};
export var selectAllActionButtons = function (state) {
    return selectActionButtonsFromPendingInventory(state).concat(selectActionButtonsFromPersistence(state), selectActionButtonsFromBlog(state), selectActionButtonsFromGrowthFund(state), selectActionButtonsFromAdventures(state), selectActionButtonsFromEventStart(state), selectActionButtonsFromSaveAccount(state), selectActionButtonsFromProcessCountdown(state), selectActionButtonsFromLevelConstraints(state), selectActionButtonFromPlayerInvite(state), selectActionButtonFromCafe(state));
};
export var selectActionButtonsFromPendingInventory = function (state) {
    if (selectPendingInventoryCount(state) <= 0) {
        return [];
    }
    var pendingInventory = selectPendingInventoryInfo(state).filter(function (entry) { return entry.typeId !== undefined; });
    var dirtyActionButtons = pendingInventory.map(function (entry) {
        var key = mapPendingInventoryTypeToAction.get(entry.typeId);
        return selectRotatableActionButtonInfoById(state, key);
    });
    return dirtyActionButtons.filter(function (entry) { return entry !== undefined; });
};
export var selectActionButtonsFromPersistence = function (state) {
    var coopActionButton = selectCoop(state) === undefined
        ? []
        : selectActionButtonFromRankUpDown(state, EventType.CoopLeague);
    return selectActionButtonFromCoopLevelUp(state).concat(coopActionButton, selectActionButtonFromRankUpDown(state, EventType.PlayerLeague), selectActionButtonFromExclusiveWebOffer(state));
};
export var selectActionButtonFromCoopLevelUp = function (state) {
    var currentLevel = selectCurrentCoopLevel(state);
    var storedLevel = utilPersistence.getValue(IdPersistence.CoopPreviousLevel);
    if (currentLevel > storedLevel) {
        var rotatableActionButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.CoopLevelUp);
        if (rotatableActionButton !== undefined) {
            return [rotatableActionButton];
        }
    }
    return [];
};
export var selectActionButtonFromRankUpDown = function (state, eventId) {
    var rankChange = selectTournamentRankProgression(state, eventId);
    if (rankChange === IdTournamentProgression.NoProgression) {
        return [];
    }
    var actionButtonId = IdActionButtonEntries.PromoteLeagueLevel;
    if (rankChange === IdTournamentProgression.Demoted) {
        actionButtonId = IdActionButtonEntries.DemoteLeagueLevel;
    }
    var rotatableActionButton = selectRotatableActionButtonInfoById(state, actionButtonId);
    if (rotatableActionButton !== undefined) {
        return [rotatableActionButton];
    }
    return [];
};
export var selectActionButtonFromExclusiveWebOffer = function (state) {
    var previousDate = utilPersistence.getValue(IdPersistence.LastWebOnlyOfferNotification);
    if (utilTime.isDifferentDay(previousDate)) {
        var rotatableActionButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.WebOnlyOffer);
        if (rotatableActionButton !== undefined) {
            return [rotatableActionButton];
        }
    }
    return [];
};
export var selectActionButtonsFromBlog = function (state) {
    var lastBlogVersion = utilBlog.getVersion() || 0;
    var currentBlogVersion = state.playerSettingsInfo.blogVersion;
    if (lastBlogVersion < currentBlogVersion) {
        var potentialButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.Blog);
        if (potentialButton !== undefined) {
            return [potentialButton];
        }
    }
    return [];
};
export var selectActionButtonsFromAdventures = function (state) {
    return [];
};
export var selectActionButtonsFromEventStart = function (state) {
    return selectActionButtonThemedEvent(state).concat(selectActionButtonsFromPrimeTimeEvent(state), selectActionButtonFromDiscountEvent(state));
};
export var selectActionButtonsFromSaveAccount = function (state) {
    return [];
};
export var selectActionButtonsFromProcessCountdown = function (state) {
    return selectActionButtonFromDogHouse(state).concat(selectActionButtonFromWaterTower(state), selectActionButtonFromCoopProjectCompleted(state));
};
export var selectActionButtonsFromLevelConstraints = function (state) {
    return selectActionButtonFromPromoteMobile(state).slice();
};
export var selectActionButtonFromPromoteMobile = function (state) {
    var level = selectPlayerLevel(state);
    if (level >= promoteMobileLevels.lowLevel &&
        level <= promoteMobileLevels.highLevel &&
        selectIterations(state, IdActionButtonEntries.PromoteMobile) === 0) {
        var potentialButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.PromoteMobile);
        if (potentialButton !== undefined) {
            return [potentialButton];
        }
    }
    return [];
};
// TODO when we implement with cafe.
export var selectActionButtonFromCafe = function (state) {
    return [];
};
export var selectActionButtonFromPlayerLeague = function (state) {
    if (selectIsEventRunningByEventType(state, EventType.PlayerLeague)) {
        return [];
    }
    return [];
};
export var selectActionButtonFromWaterTower = function (state) {
    if (selectWaterTowerCanCollectWater(state)) {
        var potentialButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.WaterTower);
        if (potentialButton !== undefined) {
            return [potentialButton];
        }
    }
    return [];
};
export var selectActionButtonFromDogHouse = function (state) {
    var limitedActionInfo = selectLimitedActionInfo(state, LimitedActionType.DOGHOUSE);
    var secondsUntilTreatReset = utilLimitedActionTime.getLimitedActionResetRemainingTime(limitedActionInfo);
    var playerCanAccessDoghouse = selectIsQualifiedForDoghouse(state);
    var shouldAdd = secondsUntilTreatReset === 0 && playerCanAccessDoghouse;
    if (shouldAdd) {
        var potentialButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.Dog);
        if (potentialButton) {
            return [potentialButton];
        }
    }
    return [];
};
export var selectActionButtonFromCoopProjectCompleted = function (state) {
    var rotatableActionButton;
    if (selectCoopCompletedProject(state) !== undefined) {
        rotatableActionButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.ProjectFinished);
    }
    return rotatableActionButton !== undefined ? [rotatableActionButton] : [];
};
export var selectActionButtonThemedEvent = function (state) {
    var themeEventInfo = selectActiveThemedEventInfo(state);
    var shouldRender = themeEventInfo != undefined &&
        (selectThemedEventCanPlayerJoin(state) ||
            (selectThemedEventIsQuestsCompleted(state) &&
                !selectThemedEventIsRewardCollected(state)));
    if (shouldRender) {
        var rotatableActionButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.ThemedEvents);
        if (rotatableActionButton !== undefined) {
            return [
                __assign({}, rotatableActionButton, { icon: themeEventInfo.icon }),
            ];
        }
    }
    return [];
};
export var selectActionButtonFromPlayerInvite = function (state) {
    if (selectHasActiveInvite(state)) {
        var rotatableActionButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.CoopInvitation);
        if (rotatableActionButton !== undefined) {
            return [rotatableActionButton];
        }
    }
    return [];
};
export var selectActionButtonFromDiscountEvent = function (state) {
    var discountEvents = [
        [EventType.ExpansionDiscount, IdActionButtonEntries.ExpansionDiscount],
        [EventType.FancyHouseDiscount, IdActionButtonEntries.PremiumHouseDiscount],
        [EventType.FertileAcreDiscount, IdActionButtonEntries.PremiumAcreDiscount],
        [EventType.OutskirtDiscount, IdActionButtonEntries.ExternalBarnDiscount],
    ];
    var actionButtons = [];
    discountEvents.forEach(function (_a) {
        var eventType = _a[0], actionButtonId = _a[1];
        var activeEvent = selectActiveDiscountEventInfoByEventType(state, eventType);
        if (activeEvent !== undefined &&
            activeEvent.proto.eventId &&
            !selectHideDiscountEventActionButton(state, activeEvent.proto.eventId)) {
            var rotatableActionButton = selectRotatableActionButtonInfoById(state, actionButtonId);
            if (rotatableActionButton) {
                actionButtons.push(rotatableActionButton);
            }
        }
    });
    return actionButtons;
};
// TODO: refactor me on friday
// By design growth fund info needs to be displayed even if the package is not purchased.
export var selectActionButtonsFromGrowthFund = function (state) {
    var growsFundCharacteristicIds = selectGrowthFundCharacteristicIds(state);
    var actionButtons = [];
    growsFundCharacteristicIds.forEach(function (id) {
        var playerLevel = selectPlayerLevel(state);
        var levelRewards = selectPackagesByRequiredOfferId(id);
        var rewardResource = selectGrowthFundRewardInfo(levelRewards);
        var icon = rewardResource != undefined && MapGrowthFundActionButtonIcon.has(rewardResource.id)
            ? MapGrowthFundActionButtonIcon.get(rewardResource.id)
            : IdIcon.HumusSeaweedIcn;
        var originalData = utilPersistence.getValue(IdPersistence.ClickedGrowthOffers, []);
        var dataToWrite = originalData.slice();
        var activatedActionButton = levelRewards.find(function (levelReward) {
            var requPlayerLevel = levelReward.requPlayerLevel || 0;
            var levelMatches = playerLevel === requPlayerLevel;
            var isClickedAlready = originalData.find(function (element) { return element.id === id && element.level === requPlayerLevel; }) != undefined;
            var isEnabled = levelMatches && !isClickedAlready && selectIsOfferPurchased(state, id);
            var maxPurchases = levelReward.maxPurchases || 1;
            var timesPurchasePackageAmount = selectTimesPurchasePackageAmount(state, levelReward.id);
            var hasBeenCollected = timesPurchasePackageAmount.greaterThanOrEqual &&
                timesPurchasePackageAmount.greaterThanOrEqual(maxPurchases);
            if (isEnabled && !hasBeenCollected) {
                dataToWrite.push({
                    id: id,
                    level: requPlayerLevel,
                });
            }
            return isEnabled && !hasBeenCollected;
        });
        if (activatedActionButton != undefined) {
            var rotatableActionButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.GoldGrowthPackageAvailable);
            if (rotatableActionButton !== undefined) {
                rotatableActionButton.action = actionPersistenceWriteBeforeAction(actionShopOpenOffer(id), IdPersistence.ClickedGrowthOffers, dataToWrite, PersistenceMode.Write);
                rotatableActionButton.icon = icon;
                actionButtons.push(rotatableActionButton);
            }
        }
    });
    return actionButtons;
};
export var selectActionButtonsFromPrimeTimeEvent = function (state) {
    var primeTimeEventInfo = selectPrimeTimeEventInfo(state);
    if (primeTimeEventInfo) {
        var isEventRunning = selectIsEventRunning(primeTimeEventInfo.proto);
        if (isEventRunning &&
            primeTimeEventInfo.proto &&
            primeTimeEventInfo.proto.eventId &&
            !selectHideDiscountEventActionButton(state, primeTimeEventInfo.proto.eventId)) {
            var rotatableActionButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.PrimeTime);
            if (rotatableActionButton) {
                return [rotatableActionButton];
            }
        }
    }
    return [];
};
export var selectActionButtonFromWebOnlyPromotion = function (state) {
    // The following line check prevents the action button from flashing while the server sends the response.
    var isDataAvailable = selectIsPurchasePackageDataAvailableForPackage(state, webExclusiveProfilePicturePackageId);
    var timesBought = selectTimesPurchasePackageAmount(state, webExclusiveProfilePicturePackageId);
    if (Long.ZERO.equals(timesBought) && isDataAvailable) {
        var rotatableActionButton = selectRotatableActionButtonInfoById(state, IdActionButtonEntries.WebExclusivePackage);
        if (rotatableActionButton) {
            return [rotatableActionButton];
        }
    }
    return [];
};



//////////////////
// WEBPACK FOOTER
// ./src/selectors/selectActionButtonInfo.ts
// module id = 880
// module chunks = 3